import type { Event as SentryEvent } from "@sentry/react";

/**
 * Scripts that are not ours and fail on their own, matched on the URL of the
 * innermost frame of the root exception — which is what Sentry's `denyUrls`
 * reads (`eventFilters.js`, `_getLastValidUrl`).
 *
 * - `iabjs://` is the scheme of the scripts the Facebook and Instagram Android
 *   in-app browsers inject into every page (`navigation_performance_logger_android`,
 *   `browser_declutter`). They throw `Java object is gone` on `beforeunload`
 *   and `Unexpected end of input` on their own — the single largest issue of
 *   the project (SHORTS-WEB-63, 70k+ events) with nothing to fix on our side.
 * - `/ekr/snippet.js` is the Zendesk widget loader (the URL `src/lib/zendesk.ts`
 *   injects), which ships its own Sentry SDK and fails to construct it on some
 *   iOS builds (SHORTS-WEB-87). Keep the two in step if Zendesk moves it.
 */
export const THIRD_PARTY_SCRIPT_DENY_URLS: Array<RegExp> = [
  /^iabjs:\/\//,
  /\/ekr\/snippet\.js/,
];

/**
 * Same family, but injected by the iOS in-app browsers **into the document
 * itself**, so the failing frame's URL is the page's own and `denyUrls` would
 * have to exclude our inline scripts too. Matched on the message instead, like
 * the browser-extension entries next to it: nothing in this codebase reads
 * `window.webkit` (SHORTS-WEB-F, SHORTS-WEB-6B) or `window._META`, the global
 * the Facebook iOS build of October 2026 assumes in its injected script
 * (`window._META.IABInnerFrame`, SHORTS-WEB-GB).
 */
export const THIRD_PARTY_SCRIPT_IGNORE_ERRORS: Array<string> = [
  "window.webkit.messageHandlers",
  "window._META.",
];

// Fetch collapses every network-level failure — offline, DNS, a refused CORS
// preflight, a request an extension blocked, a connection dropped mid-flight —
// into one opaque `TypeError`, deliberately, so that nothing cross-origin
// leaks. It carries no structured cause, only a message each engine words its
// own way: Chromium "Failed to fetch", WebKit "Load failed", Firefox
// "NetworkError when attempting to fetch resource". Note none of these is an
// `AbortController` abort — that rejects with an `AbortError` and is not
// matched here.
const NETWORK_FAILURE = /failed to fetch|load failed|networkerror/i;

/**
 * True for the background noise among those failures: the ones nobody acts on,
 * because nothing reported them on purpose.
 *
 * This cannot live in `ignoreErrors`. That matches on the message alone and
 * runs ahead of `beforeSend`, so it also drops the reports our own catch sites
 * send deliberately — `reportPaymentError`, `reportPlayerIssue`,
 * `useSectionContent`, and half a dozen more.
 * That is "this payment didn't go through, why?" going unanswerable.
 *
 * `mechanism.handled` is what tells the two apart, and the **SDK** stamps it,
 * never the call site: `false` for what `onerror` / `onunhandledrejection`
 * picked up on their own, `true` for an explicit `captureException`. A shared
 * tag would be an allowlist instead, and it would fail the wrong way — a new
 * catch site that forgot the tag would be silently dropped again.
 *
 * The comparison is `=== false`, not `!== true`, so anything the SDK left
 * unstamped is kept: this filter fails open.
 *
 * It reads the **last** value, not the first: `linkedErrors` prepends the
 * `cause` chain, so the error that was actually thrown sits last, and a chained
 * cause is stamped `handled: true` whatever its parent's mechanism says.
 */
export const isUnhandledNetworkNoise = (event: SentryEvent): boolean => {
  const values = event.exception?.values;
  const exception = values?.[values.length - 1];

  // Same surface `ignoreErrors` matches on — `type: value` — so a DOMException
  // whose `name` is "NetworkError" (XHR, sendBeacon) is caught by its type,
  // whatever its message says.
  return (
    exception?.mechanism?.handled === false &&
    NETWORK_FAILURE.test(`${exception.type}: ${exception.value}`)
  );
};
